/**
 * Per-event seen / clicked counters on `v2-events`.
 * Fire-and-forget from public UI; failures never break product flows.
 */

import { doc, increment, updateDoc } from 'firebase/firestore';
import { db, isFirestoreAvailable } from '../../firebase';
import { shouldSkipAnalytics } from '../../analytics/analyticsActor';
import { trackUxEvent } from '../shared/uxEvents';

export const EVENTS_COLLECTION = 'v2-events';
export const EVENT_SEEN_STORAGE_PREFIX = 'm2b_event_seen_';
export const EVENT_CLICKED_STORAGE_PREFIX = 'm2b_event_clicked_';

const claimedKeys = new Set();

/**
 * @param {'seen'|'clicked'} kind
 * @param {string} eventId
 * @returns {string}
 */
export function eventStatStorageKey(kind, eventId) {
  const prefix =
    kind === 'clicked' ? EVENT_CLICKED_STORAGE_PREFIX : EVENT_SEEN_STORAGE_PREFIX;
  return `${prefix}${eventId}`;
}

/**
 * @param {unknown} value
 * @returns {number}
 */
export function eventStatCount(value) {
  const n = Math.floor(Number(value));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * @param {object} event
 * @returns {{ seen: number, clicked: number }}
 */
export function eventRowStats(event) {
  return {
    seen: eventStatCount(event?.seenCount),
    clicked: eventStatCount(event?.clickCount),
  };
}

/**
 * @param {object[]} events
 * @returns {{ seen: number, clicked: number, events: number }}
 */
export function sumEventStats(events) {
  const list = Array.isArray(events) ? events : [];
  return list.reduce(
    (acc, event) => {
      const row = eventRowStats(event);
      acc.seen += row.seen;
      acc.clicked += row.clicked;
      return acc;
    },
    { seen: 0, clicked: 0, events: list.length }
  );
}

/**
 * @param {object} a
 * @param {object} b
 * @param {'title'|'date'|'seen'|'clicked'} sortKey
 * @param {'asc'|'desc'} dir
 * @returns {number}
 */
export function compareEventAnalytics(a, b, sortKey, dir) {
  const mul = dir === 'asc' ? 1 : -1;
  const statsA = eventRowStats(a);
  const statsB = eventRowStats(b);

  let primary = 0;
  if (sortKey === 'seen') primary = statsA.seen - statsB.seen;
  else if (sortKey === 'clicked') primary = statsA.clicked - statsB.clicked;
  else if (sortKey === 'date') {
    primary = String(a?.dateStr || '').localeCompare(String(b?.dateStr || ''));
  } else {
    primary = String(a?.title || '').localeCompare(String(b?.title || ''), undefined, {
      sensitivity: 'base',
    });
  }
  if (primary) return primary * mul;

  const clickTie = statsB.clicked - statsA.clicked;
  if (clickTie) return clickTie;
  const seenTie = statsB.seen - statsA.seen;
  if (seenTie) return seenTie;
  return String(a?.title || '').localeCompare(String(b?.title || ''), undefined, {
    sensitivity: 'base',
  });
}

function hasStorageFlag(key) {
  if (typeof sessionStorage === 'undefined') return false;
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function setStorageFlag(key) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(key, '1');
  } catch {
    // Ignore quota / private-mode failures; in-memory claim still holds.
  }
}

/**
 * Claim a once-per-session slot. Same-tick duplicate cards share this Set.
 * @param {string} key
 * @returns {boolean} true when this caller owns the increment
 */
function claimSessionKey(key) {
  if (claimedKeys.has(key) || hasStorageFlag(key)) return false;
  claimedKeys.add(key);
  setStorageFlag(key);
  return true;
}

/**
 * @param {string} eventId
 * @param {'seenCount'|'clickCount'} field
 * @returns {Promise<boolean>}
 */
async function incrementEventField(eventId, field) {
  if (!eventId || !isFirestoreAvailable() || !db) return false;
  try {
    await updateDoc(doc(db, EVENTS_COLLECTION, eventId), {
      [field]: increment(1),
    });
    return true;
  } catch (err) {
    console.warn('eventStats increment failed:', err?.message || err);
    return false;
  }
}

/**
 * @param {string} eventId
 * @param {{ title?: string, source?: string }} [meta]
 * @returns {Promise<boolean>}
 */
export async function recordEventSeen(eventId, meta = {}) {
  if (shouldSkipAnalytics()) return false;
  const id = String(eventId || '').trim();
  if (!id) return false;
  if (!claimSessionKey(eventStatStorageKey('seen', id))) return false;

  const ok = await incrementEventField(id, 'seenCount');
  trackUxEvent({
    name: 'event_impression',
    eventId: id,
    title: meta.title || '',
    source: meta.source || 'list',
  });
  return ok;
}

/**
 * Click also records seen when this session has not counted an impression yet.
 * @param {string} eventId
 * @param {{ title?: string, source?: string }} [meta]
 * @returns {Promise<boolean>}
 */
export async function recordEventClicked(eventId, meta = {}) {
  if (shouldSkipAnalytics()) return false;
  const id = String(eventId || '').trim();
  if (!id) return false;

  await recordEventSeen(id, meta);

  if (!claimSessionKey(eventStatStorageKey('clicked', id))) return false;

  const ok = await incrementEventField(id, 'clickCount');
  trackUxEvent({
    name: 'event_open',
    eventId: id,
    title: meta.title || '',
    source: meta.source || 'list',
  });
  return ok;
}

/** Test helper — clears in-memory session claims. */
export function __resetEventStatsForTests() {
  claimedKeys.clear();
}
